
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from '@/components/ui/use-toast';
import { Edit, Trash2, PlusCircle, Upload } from 'lucide-react';
import BusinessService, { BusinessProgress, FundingRequirement } from '@/services/BusinessService';

const FundingRequirementsManager = () => {
  const [businesses, setBusinesses] = useState<BusinessProgress[]>([]);
  const [fundingRequirements, setFundingRequirements] = useState<FundingRequirement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentRequirement, setCurrentRequirement] = useState<FundingRequirement | null>(null);
  const [formData, setFormData] = useState({
    business_id: '',
    amount: '',
    next_step: '',
    fund_allocation_image: null as string | null
  });

  // Fetch data
  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [businessesData, requirementsData] = await Promise.all([
        BusinessService.getBusinessProgress(),
        BusinessService.getFundingRequirements()
      ]);
      setBusinesses(businessesData);
      setFundingRequirements(requirementsData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load data',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Reset form data
  const resetForm = () => {
    setFormData({
      business_id: '',
      amount: '',
      next_step: '',
      fund_allocation_image: null
    });
    setCurrentRequirement(null);
  };

  // Handle form opening
  const handleAddNew = () => {
    resetForm();
    setIsEditMode(false);
    setIsDialogOpen(true);
  };

  // Handle edit form opening
  const handleEdit = (requirement: FundingRequirement) => {
    const business = businesses.find(b => b.id === requirement.business_id);
    
    setFormData({
      business_id: requirement.business_id,
      amount: requirement.amount.toString(),
      next_step: requirement.next_step,
      fund_allocation_image: requirement.fund_allocation_image
    });
    
    setCurrentRequirement(requirement);
    setIsEditMode(true);
    setIsDialogOpen(true);
  };

  // Handle delete
  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this funding requirement?')) {
      return;
    }
    
    try {
      await BusinessService.deleteFundingRequirement(id);
      toast({
        title: 'Success',
        description: 'Funding requirement deleted successfully',
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting funding requirement:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete funding requirement',
        variant: 'destructive',
      });
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.business_id) {
      toast({
        title: 'Error',
        description: 'Please select a business',
        variant: 'destructive',
      });
      return;
    }
    
    if (!formData.amount || isNaN(parseFloat(formData.amount))) {
      toast({
        title: 'Error',
        description: 'Please enter a valid amount',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      const data = {
        business_id: formData.business_id,
        amount: parseFloat(formData.amount),
        next_step: formData.next_step,
        fund_allocation_image: formData.fund_allocation_image
      };
      
      if (isEditMode && currentRequirement) {
        await BusinessService.updateFundingRequirement(currentRequirement.id, data);
        toast({
          title: 'Success',
          description: 'Funding requirement updated successfully',
        });
      } else {
        await BusinessService.createFundingRequirement(data);
        toast({
          title: 'Success',
          description: 'Funding requirement created successfully',
        });
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving funding requirement:', error);
      toast({
        title: 'Error',
        description: 'Failed to save funding requirement',
        variant: 'destructive',
      });
    }
  };

  // Get business name from ID
  const getBusinessName = (id: string) => {
    const business = businesses.find(b => b.id === id);
    return business ? business.business_name : 'Unknown';
  };

  // Format amount to currency
  const formatCurrency = (amount: number) => {
    return `R${amount.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Handle form change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Funding Requirements</CardTitle>
            <CardDescription>Manage funding requirements for businesses</CardDescription>
          </div>
          <Button onClick={handleAddNew}>
            <PlusCircle className="mr-2 h-4 w-4" />
            Add Requirement
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-10 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-solid border-current border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]"></div>
              <p className="mt-2 text-sm text-gray-500">Loading funding requirements...</p>
            </div>
          ) : fundingRequirements.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-gray-500">No funding requirements found</p>
              <Button variant="outline" className="mt-4" onClick={handleAddNew}>
                Add your first requirement
              </Button>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Business</TableHead>
                    <TableHead>Amount (ZAR)</TableHead>
                    <TableHead>Next Step</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fundingRequirements.map((requirement) => (
                    <TableRow key={requirement.id}>
                      <TableCell className="font-medium">{getBusinessName(requirement.business_id)}</TableCell>
                      <TableCell>{formatCurrency(requirement.amount)}</TableCell>
                      <TableCell>{requirement.next_step}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end space-x-2">
                          <Button variant="outline" size="sm" onClick={() => handleEdit(requirement)}>
                            <Edit className="h-4 w-4" />
                            <span className="sr-only">Edit</span>
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleDelete(requirement.id)}>
                            <Trash2 className="h-4 w-4" />
                            <span className="sr-only">Delete</span>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isEditMode ? 'Edit Funding Requirement' : 'Add Funding Requirement'}</DialogTitle>
            <DialogDescription>
              {isEditMode 
                ? 'Update the funding requirement details.' 
                : 'Create a new funding requirement for a business.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="business_id">Business</Label>
                <Select 
                  value={formData.business_id} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, business_id: value }))}
                  disabled={isEditMode}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a business" />
                  </SelectTrigger>
                  <SelectContent>
                    {businesses.map((business) => (
                      <SelectItem key={business.id} value={business.id}>
                        {business.business_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="amount">Amount (ZAR)</Label>
                <Input
                  id="amount"
                  name="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={handleInputChange}
                  placeholder="e.g. 1000000"
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="next_step">Next Step</Label>
                <Textarea
                  id="next_step"
                  name="next_step"
                  value={formData.next_step}
                  onChange={handleInputChange}
                  placeholder="Describe the next step"
                  rows={3}
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="fund_allocation_image">Fund Allocation Image (Optional)</Label>
                <div className="flex">
                  <Input
                    id="fund_allocation_image"
                    name="fund_allocation_image"
                    value={formData.fund_allocation_image || ''}
                    onChange={handleInputChange}
                    placeholder="Image URL"
                    className="flex-1"
                  />
                  <Button type="button" variant="outline" size="icon" className="ml-2">
                    <Upload className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-gray-500">You can link to an existing image or upload a new one</p>
              </div>
            </div>
            <DialogFooter>
              <Button type="submit">{isEditMode ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FundingRequirementsManager;
